import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Button } from "@comps/ui/button";
import { post } from "@utils/utils";
import { useState } from "react";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import { ObjectDto } from "@dto/objects";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_NewObjectButton, TW_NewObjectButtonIcon, TW_NewObjectButtonText, useHover } from "@utils/twClasses";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { translate } from "src/i18n/i18n";
import { AddSquare } from "iconsax-react";

interface NewObjectButtonProps {
  onNewObject: (object: ObjectDto) => void;
}

function NewObjectButton({ onNewObject }: NewObjectButtonProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsColor = useModelStore(state => state.wsColor);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const addObjectHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor, wsColor));

  const objectAdded = (object: ObjectDto) => {
    onNewObject(object);
    setIsDialogOpen(false);
  }


  const cancel = () => {
    setIsDialogOpen(false);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} >
      <DialogTrigger className={TW_NewObjectButton} {...addObjectHover}>
        <AddSquare className={TW_NewObjectButtonIcon} />
        <p className={TW_NewObjectButtonText}>{t("object.button.new.text")}</p>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("object.dialog.new.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.new.description")}</DialogDescription>
        </DialogHeader>
        <NewObjectForm onNewObject={objectAdded} onCancel={cancel} />
      </DialogContent>
    </Dialog>
  )
}

function NewObjectForm({ onNewObject, onCancel }: { onNewObject: (object: ObjectDto) => void, onCancel: () => void }) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const objectInfos = useModelStore(state => state.objectInfos);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    objectName: z.string().min(1, {
      message: t("object.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      objectName: t("object.dialog.name.default.value"),
    },
  })

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace) {
      console.error("No workspace selected");
      return;
    }
    let order = 0;
    if (objectInfos.length > 0) {
      const lastObj = objectInfos[objectInfos.length - 1].object;
      if (lastObj.order) {
        order = lastObj.order + DEFAULT_ORDER_INCREMENT;
      } else {
        order = DEFAULT_ORDER_INCREMENT;
      }
    }

    const body = {
      name: values.objectName,
      order: order,
    }
    post<ObjectDto>(`${BASE_URL}/model/workspaces/${workspace._key}/objects`, body).then(newObject => {
      if (newObject) {
        if (isCompozzError(newObject)) {
          const cError = newObject as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {          
          setError(null);
          onNewObject(newObject as ObjectDto);
        }
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="objectName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("object.dialog.name.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("object.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default NewObjectButton;
